import { Component, computed, inject, input } from "@angular/core";
import { Router } from "@angular/router";
import { SharedModule } from "../../shared/shared.module";
import type { Artifact, LinkCard } from "../xwai.types";

/**
 * Renders the structured `artifacts` returned alongside an assistant message.
 *
 * Each artifact is a discriminated union keyed by `type`. The template switches
 * on `type` and IGNORES unknown types, so adding a new artifact kind is a
 * one-place change (a new `@case`/`@if` branch here) and an unknown type from
 * the backend never crashes the chat.
 */
@Component({
  selector: "app-xwai-artifacts",
  templateUrl: "./xwai-artifacts.component.html",
  styleUrls: ["./xwai-artifacts.component.less"],
  imports: [SharedModule],
})
export class XwaiArtifactsComponent {
  private router = inject(Router);

  /** May be null/[]; both render nothing. */
  readonly artifacts = input<Artifact[] | null | undefined>(null);

  /** Non-null artifacts, ready to iterate in the template. */
  readonly items = computed<Artifact[]>(() => this.artifacts() ?? []);

  /**
   * Icon keys we know our ant-design set renders. An unrecognized `icon` is
   * omitted rather than passed to `nz-icon` (which would warn/error).
   */
  private static readonly KNOWN_ICONS = new Set([
    "fund",
    "table",
    "line-chart",
    "dot-chart",
    "bar-chart",
    "area-chart",
    "pie-chart",
    "heat-map",
    "link",
    "eye",
    "file-text",
    "appstore",
    "project",
    "experiment",
  ]);

  /** The icon name to render for a card, or null to omit it. */
  iconOf(card: LinkCard): string | null {
    const icon = card.icon;
    return icon && XwaiArtifactsComponent.KNOWN_ICONS.has(icon) ? icon : null;
  }

  /**
   * Open a link. Same-origin dashboard routes go through the in-app router;
   * anything else opens in a new tab. Falls back to a new tab if the URL can't
   * be parsed or navigation fails.
   */
  open(card: LinkCard, event: MouseEvent): void {
    const url = card?.url;
    if (!url) return;

    // Let modifier-clicks (open in new tab/window) behave natively.
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1) {
      return;
    }

    let parsed: URL;
    try {
      parsed = new URL(url, window.location.origin);
    } catch {
      this.openExternal(url);
      return;
    }

    event.preventDefault();
    if (parsed.origin === window.location.origin) {
      this.router
        .navigateByUrl(parsed.pathname + parsed.search + parsed.hash)
        .catch(() => this.openExternal(url));
    } else {
      this.openExternal(url);
    }
  }

  private openExternal(url: string): void {
    window.open(url, "_blank", "noopener");
  }
}
